import { useState } from 'react'
import {
  Stack, Text, Table, Button, Badge, Modal,
  TextInput, Textarea, Group, Alert, MultiSelect,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconTrash, IconAlertCircle } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useCurrentTenant } from '@/store/auth'
import { DataCard } from '@/components/ui'

interface FreezePeriod {
  id: number
  name: string
  reason: string | null
  starts_at: string
  ends_at: string
  applies_to: string[]
}

function fmt(dt: string) {
  return new Date(dt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
}

export function GmudFreezeAdmin() {
  const tenantId = useCurrentTenant()?.id
  const queryClient = useQueryClient()
  const [opened, setOpened] = useState(false)
  const [name, setName] = useState('')
  const [reason, setReason] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [appliesTo, setAppliesTo] = useState<string[]>(['normal', 'urgent'])

  const { data: periods = [], isLoading, error } = useQuery<FreezePeriod[]>({
    queryKey: ['freeze-periods', tenantId],
    queryFn: async () => (await api.get<FreezePeriod[]>(`/v1/tenants/${tenantId}/gmud/freeze-periods`)).data,
    enabled: !!tenantId,
  })

  const createMutation = useMutation({
    mutationFn: async () =>
      api.post(`/v1/tenants/${tenantId}/gmud/freeze-periods`, {
        name: name.trim(), reason: reason.trim() || undefined,
        starts_at: startsAt, ends_at: endsAt, applies_to: appliesTo,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freeze-periods', tenantId] })
      notifications.show({ color: 'green', message: 'Freeze period criado.' })
      setOpened(false); setName(''); setReason(''); setStartsAt(''); setEndsAt('')
    },
    onError: () => notifications.show({ color: 'red', message: 'Erro ao criar freeze period.' }),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) =>
      api.delete(`/v1/tenants/${tenantId}/gmud/freeze-periods/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freeze-periods', tenantId] })
      notifications.show({ color: 'green', message: 'Freeze period removido.' })
    },
  })

  return (
    <Stack gap="md">
      {error && <Alert icon={<IconAlertCircle size={16} />} color="red">Não foi possível carregar os períodos de congelamento. Tente novamente.</Alert>}
      <DataCard flush
        title="Períodos de Congelamento (Change Freeze)"
        accent="primary"
        badge={{ value: periods.length, color: 'blue' }}
        loading={isLoading}
        extra={
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setOpened(true)}>
            Novo Freeze Period
          </Button>
        }
      >
        {periods.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="xl" px="md">Nenhum período de congelamento cadastrado. Clique em &quot;Novo Freeze Period&quot; para definir janelas de bloqueio.</Text>
        ) : (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Início</Table.Th>
                <Table.Th>Fim</Table.Th>
                <Table.Th>Aplica a</Table.Th>
                <Table.Th>Ações</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {periods.map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td fw={500}>{p.name}</Table.Td>
                  <Table.Td><Text size="xs">{fmt(p.starts_at)}</Text></Table.Td>
                  <Table.Td><Text size="xs">{fmt(p.ends_at)}</Text></Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {(p.applies_to ?? []).map((t) => (
                        <Badge key={t} size="xs" variant="outline">{t}</Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs" color="red" variant="subtle"
                      leftSection={<IconTrash size={12} />}
                      onClick={() => deleteMutation.mutate(p.id)}
                      loading={deleteMutation.isPending}
                    >
                      Remover
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </DataCard>

      <Modal opened={opened} onClose={() => setOpened(false)} title="Novo Freeze Period" size="md">
        <Stack gap="sm">
          <TextInput label="Nome" required value={name} onChange={(e) => setName(e.target.value)} />
          <Textarea label="Motivo" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          <Group grow>
            <TextInput label="Início (ISO 8601)" placeholder="2026-11-25T00:00:00Z" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            <TextInput label="Fim (ISO 8601)" placeholder="2026-11-30T23:59:00Z" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          </Group>
          <MultiSelect
            label="Aplica-se a classificações"
            value={appliesTo}
            onChange={setAppliesTo}
            data={[
              { value: 'normal', label: 'Normal' },
              { value: 'urgent', label: 'Urgente' },
              { value: 'change_request', label: 'Change Request' },
            ]}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setOpened(false)}>Cancelar</Button>
            <Button onClick={() => createMutation.mutate()} loading={createMutation.isPending}>Criar</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
